/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   JobDetail.tsx
 *     Feature         :   114-job-infrastructure
 *     Description     :   Job run detail modal with logs, status, and metadata
 *     Constitution    :   v1.11.0 | Principle XXXVIII (Specification Testing Standards)
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-28
 * =========================================================================================
 */

import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

import { Card, Badge, Button, Alert, Spinner, Tabs, DescriptionList } from '../../components/ui';
import type { DescriptionItem } from '../../components/ui';
import { AppModal } from '../../components/ui/AppModal';
import { getJobRun, getJobDefinition, JobRun, JobDefinition } from '../../services/jobService';
import { JobLogs } from './JobLogs';
import JobStatusBadge from './JobStatusBadge';

/**
 * Props for JobDetail component
 */
interface JobDetailProps {
  open: boolean;
  jobRunId: string | null;
  onClose: () => void;
}

/** Icon-button styling (token-driven; replaces MUI IconButton). */
const iconBtn =
  'inline-flex items-center justify-center rounded-[var(--radius-md)] p-1.5 ' +
  'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] hover:text-[var(--color-text-primary)] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

/** Section heading inside the panels. */
const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h6 className="mb-2 text-base font-bold text-[var(--color-text-primary)]">{children}</h6>
);

/**
 * JobDetail Component
 * Modal dialog showing job run details and logs
 */
export const JobDetail: React.FC<JobDetailProps> = ({
  open,
  jobRunId,
  onClose,
}) => {
  // State
  const [jobRun, setJobRun] = useState<JobRun | null>(null);
  const [jobDefinition, setJobDefinition] = useState<JobDefinition | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  /**
   * Fetch job run and definition details
   */
  useEffect(() => {
    const fetchData = async () => {
      if (!jobRunId) return;

      try {
        setLoading(true);
        setError(null);

        const run = await getJobRun(jobRunId);
        if (!run) {
          setError('Job run not found');
          return;
        }
        setJobRun(run);

        // Fetch associated definition
        const def = await getJobDefinition(run.job_definition_id);
        setJobDefinition(def);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch job details');
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchData();
    } else {
      // Reset state when closing
      setJobRun(null);
      setJobDefinition(null);
      setActiveTab(0);
    }
  }, [open, jobRunId]);

  /**
   * Refresh job run data
   */
  const handleRefresh = async () => {
    if (!jobRunId) return;

    try {
      setLoading(true);
      const run = await getJobRun(jobRunId);
      setJobRun(run);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Format timestamp for display
   */
  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) return '-';
    try {
      return format(new Date(timestamp), 'yyyy-MM-dd HH:mm:ss');
    } catch {
      return timestamp;
    }
  };

  /**
   * Calculate duration between two timestamps
   */
  const calculateDuration = (start: string | null | undefined, end: string | null | undefined) => {
    if (!start) return '-';
    try {
      const startDate = new Date(start);
      const endDate = end ? new Date(end) : new Date();
      const diffMs = endDate.getTime() - startDate.getTime();

      if (diffMs < 1000) return `${diffMs}ms`;
      if (diffMs < 60000) return `${Math.round(diffMs / 1000)}s`;
      if (diffMs < 3600000) return `${Math.round(diffMs / 60000)}m`;
      return `${Math.round(diffMs / 3600000)}h ${Math.round((diffMs % 3600000) / 60000)}m`;
    } catch {
      return '-';
    }
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      size="xl"
      title={
        <div className="flex w-full items-center gap-2 pr-2">
          <span>Job Run #{jobRunId}</span>
          {jobRun && <JobStatusBadge status={jobRun.status} />}
          <button
            className={`${iconBtn} ml-auto`}
            onClick={handleRefresh}
            disabled={loading}
            aria-label="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      }
      footer={<Button variant="ghost" onClick={onClose}>Close</Button>}
    >
      {loading && !jobRun && (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      )}

      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}

      {jobRun && (
        <>
          <Tabs
            tabs={[
              { key: '0', label: 'Overview' },
              { key: '1', label: 'Logs' },
              { key: '2', label: 'Configuration' },
            ]}
            value={String(activeTab)}
            onChange={(k) => setActiveTab(Number(k))}
          />

          {/* Overview Tab */}
          {activeTab === 0 && (
            <div className="py-4">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Status & Timing */}
                <Card>
                  <SectionTitle>Execution Status</SectionTitle>
                  <DescriptionList
                    items={[
                      { label: 'Status', value: <JobStatusBadge status={jobRun.status} /> },
                      {
                        label: 'Trigger Type',
                        value: <Badge variant="default">{jobRun.trigger_type}</Badge>,
                      },
                      { label: 'Triggered By', value: jobRun.triggered_by || '-' },
                      { label: 'Started At', value: formatTimestamp(jobRun.started_at) },
                      { label: 'Completed At', value: formatTimestamp(jobRun.completed_at) },
                      {
                        label: 'Duration',
                        value: calculateDuration(jobRun.started_at, jobRun.completed_at),
                      },
                    ]}
                  />
                  {jobRun.error_message && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-[var(--color-status-danger)]">
                        Error Message
                      </p>
                      <Alert variant="error" className="mt-1">
                        {jobRun.error_message}
                      </Alert>
                    </div>
                  )}
                </Card>

                {/* Job Definition Info */}
                <Card>
                  <SectionTitle>Job Definition</SectionTitle>
                  {jobDefinition ? (
                    <DescriptionList
                      items={[
                        { label: 'Job Name', value: jobDefinition.job_name || '-' },
                        {
                          label: 'Job Type',
                          value: <Badge variant="primary">{jobDefinition.job_type}</Badge>,
                        },
                        {
                          label: 'Container Image',
                          value: <span className="font-mono">{jobDefinition.container_image}</span>,
                        },
                        { label: 'Timeout', value: `${jobDefinition.timeout_minutes} minutes` },
                        { label: 'Max Retries', value: jobDefinition.max_retries },
                      ]}
                    />
                  ) : (
                    <p className="text-[var(--color-text-secondary)]">Definition not found</p>
                  )}
                </Card>

                {/* Identifiers */}
                <Card className="md:col-span-2">
                  <SectionTitle>Identifiers &amp; Tracking</SectionTitle>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
                    <div>
                      <p className="text-xs text-[var(--color-text-secondary)]">Run ID</p>
                      <p className="font-mono text-[var(--color-text-primary)]">{jobRun.run_id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[var(--color-text-secondary)]">Correlation ID</p>
                      <p className="font-mono text-[var(--color-text-primary)]">
                        {jobRun.correlation_id || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[var(--color-text-secondary)]">Capacity ID</p>
                      <p className="font-mono text-[var(--color-text-primary)]">
                        {jobRun.capacity_id || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[var(--color-text-secondary)]">Client ID</p>
                      <p className="font-mono text-[var(--color-text-primary)]">
                        {jobRun.client_id || '-'}
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          )}

          {/* Logs Tab */}
          {activeTab === 1 && (
            <div className="py-4">
              <JobLogs
                jobRunId={jobRunId!}
                autoRefresh={jobRun.status === 'RUNNING'}
              />
            </div>
          )}

          {/* Configuration Tab */}
          {activeTab === 2 && (
            <div className="py-4">
              <Card>
                <SectionTitle>Runtime Parameters</SectionTitle>
                {jobRun.parameters && Object.keys(jobRun.parameters).length > 0 ? (
                  <pre className="overflow-auto rounded-[var(--radius-md)] bg-[var(--color-bg-subtle)] p-4 font-mono text-[0.85rem] text-[var(--color-text-primary)]">
                    {JSON.stringify(jobRun.parameters, null, 2)}
                  </pre>
                ) : (
                  <p className="text-[var(--color-text-secondary)]">No custom parameters</p>
                )}

                {jobDefinition?.default_parameters && (
                  <div className="mt-6">
                    <SectionTitle>Default Parameters (from Definition)</SectionTitle>
                    <pre className="overflow-auto rounded-[var(--radius-md)] bg-[var(--color-bg-subtle)] p-4 font-mono text-[0.85rem] text-[var(--color-text-primary)]">
                      {JSON.stringify(jobDefinition.default_parameters, null, 2)}
                    </pre>
                  </div>
                )}

                {jobRun.container_group_name && (
                  <div className="mt-6">
                    <SectionTitle>Container Information</SectionTitle>
                    <DescriptionList
                      items={[
                        {
                          label: 'Container Group',
                          value: <span className="font-mono">{jobRun.container_group_name}</span>,
                        },
                        ...(jobRun.exit_code !== undefined && jobRun.exit_code !== null
                          ? [
                              {
                                label: 'Exit Code',
                                value: (
                                  <Badge variant={jobRun.exit_code === 0 ? 'success' : 'error'}>
                                    {jobRun.exit_code}
                                  </Badge>
                                ),
                              } as DescriptionItem,
                            ]
                          : []),
                      ]}
                    />
                  </div>
                )}
              </Card>
            </div>
          )}
        </>
      )}
    </AppModal>
  );
};

export default JobDetail;
